import {
  DEFAULT_DATA_EXTRACTION_CONFIG,
  DEFAULT_INHERIT_PARENT_TYPE,
  InboxDbConfig
} from "@app/core-app/app/data/profile";
import {useFormik} from "formik";
import React, {ChangeEvent, forwardRef, useImperativeHandle, useState} from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Select,
  Stack, Switch,
  Text,
  VStack
} from "@chakra-ui/react";
import {CopyIcon} from "@chakra-ui/icons";
import {copyToClipboard} from "../../../services/utils";
import {CheckIcon, TrashIcon, XIcon, LightningBoltIcon, DatabaseIcon} from "@heroicons/react/outline";
import {InboxPropertyConfiguration} from "@app/core-app/app/domain/notion/db-properties";
import {useStatusToast} from "../../../hooks/useStatusToast";
import {ViewInboxLogButton} from "../organisms/ViewInboxLogModal";
import {CollapsibleSection} from "../atoms/CollapsibleSection";
import {DatabaseInfo} from "@app/core-app/app/data/notion/DatabaseInfo";
import {CreateNewAIProperty} from "../organisms/CreateNewAIProperty";
import {CardRow} from "../organisms/DatabaseCard/CardRow";
import MultiSelect from "../atoms/MultiSelect";
import {getNonReservedPropertiesNames} from "@app/core-app/app/domain/notion/properties";
import {DropdownSelector} from "../atoms/DropdownSelector";
import {event} from "../../../services/analytics";
import {DefaultPropertiesModal} from "../organisms/DefaultPropertiesModal";
import {DefaultNotionProperty} from "@app/core-app/app/data/notion/properties";
// import {HiLightningBolt} from "react-icons/hi";

const InboxConfiguration = (
  {onChange, value}: {
    onChange: (config: InboxDbConfig) => void,
    value: InboxDbConfig
  }) => {
  const formik = useFormik({
    initialValues: value,
    onSubmit: (values) => {
      if (onChange) {
        onChange(values)
      }
    },
  })

  const onChangeWrapped = (e: string | ChangeEvent) => {
    formik.handleChange(e)
    formik.handleSubmit()
  }


  return <Stack direction={["column", "column", "row"]} alignItems={["start", "start", "center"]} spacing={"4"}>
    <VStack alignItems={"start"} spacing={"0"} maxW={["md"]}>
      <Text textColor={"black"} fontWeight={"medium"}>Save email as</Text>
      <Text textColor={"gray.700"}>Choose between plain text and email widget. Email widget is good for rich media
        emails.</Text>
    </VStack>


    <Flex>
      <form onSubmit={formik.handleSubmit}>
        <Select id={"saveAs"} onChange={onChangeWrapped} value={formik.values.saveAs ?? "plain_text"}
          style={{padding: "0px 36px 0px 16px"}}>
          <option value='plain_text'>Save as plain text</option>
          <option value='embed'>Embed email widget</option>
        </Select>
      </form>
    </Flex>

  </Stack>
}

export interface IDataExtractionSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

export interface IDataExtractionSwitchRef {
  reset: () => void;
}

export const DataExtractionSwitch = forwardRef<IDataExtractionSwitchRef, IDataExtractionSwitchProps>(({
  enabled,
  onChange
}, ref) => {
  const [checked, setChecked] = useState(enabled);

  useImperativeHandle(ref, () => ({
    reset: () => setChecked(enabled)
  }));

  return (
    <Switch
      colorScheme={"blue"} id='extractionEnbled'
      isChecked={checked}
      onChange={(e) => {
        const isChecked = e.target.checked;
        setChecked(isChecked)
        if (onChange) {
          onChange(isChecked)
        }
      }}
    />
  );
});


export type ReceiveEmailsTabProps = {
  database: DatabaseInfo
  inboxEmail: string | null | undefined
  inboxProperties: InboxPropertyConfiguration[]
  inboxConfiguration: InboxDbConfig
  onCreateInbox: () => void
  onDeleteInbox: () => void
  onCreateNewAIProperty: (data: any) => Promise<any>
  onDropAIProperty: (propertyName: string) => Promise<any>
  onCreateNewDefaultProperty: (data: DefaultNotionProperty) => Promise<any>
  onDropDefaultProperty: (propertyName: string) => Promise<any>
  onAutoconfigure: () => void
  onConfigurationChange: (config: InboxDbConfig) => void

}

export const ReceiveEmailsTab = ({
  database,
  inboxEmail,
  inboxProperties,
  inboxConfiguration,
  onCreateInbox,
  onDeleteInbox,
  onCreateNewAIProperty,
  onDropAIProperty,
  onCreateNewDefaultProperty,
  onDropDefaultProperty,
  onConfigurationChange,
  onAutoconfigure
}: ReceiveEmailsTabProps) => {

  const databaseId = database.id
  const {addToast} = useStatusToast()
  const configured = inboxProperties.filter(x => !x.optional).every(p => p.property)

  return <VStack alignItems={"start"} w={"full"} spacing={"8"} paddingY={"2"}>
    <Stack direction={["column", "column", "row"]} alignItems={["start", "start", "center"]} spacing={"4"}>
      <VStack alignItems={"start"} spacing={"0"} maxW={"md"}>
        <Text textColor={"black"} fontWeight={"medium"}>Email Address</Text>
        <Text textColor={"gray.700"}>Send or forward emails to the address and emails will be saved in the Notion</Text>
      </VStack>

      {!inboxEmail &&
        <Button
          colorScheme={"blue"}
          size={"sm"}
          onClick={() => {
            if (onCreateInbox) {
              onCreateInbox()
            }
          }}>
          <span>
          Create Email Address
          </span>
        </Button>
      }

      {inboxEmail &&
        <HStack spacing={"2"}>
          <Text fontWeight={"medium"}>{inboxEmail}</Text>
          <CopyIcon h={5} w={5}
            onClick={() => {
              copyToClipboard(inboxEmail)
              addToast({title: "Copied 🎉", status: "success"})
            }}
            cursor={"pointer"}
            _hover={{color: "blue"}}
            _active={{backgroundColor: "blue.100"}}/>
        </HStack>
      }
    </Stack>


    <InboxConfiguration value={inboxConfiguration}
      onChange={(inboxConfig) => {
        if (onConfigurationChange) {
          onConfigurationChange(inboxConfig)
        }
      }}/>

    <Stack direction={["column", "column", "row"]} alignItems={["start", "start", "center"]} spacing={"4"}>
      <VStack alignItems={"start"} spacing={"0"} maxW={"md"}>
        <Text textColor={"black"} fontWeight={"medium"}>Emails Log</Text>
        <Text textColor={"gray.700"}>View your latest saved emails and their status.
          Check if your emails are saved successfully.</Text>
      </VStack>

      <ViewInboxLogButton databaseId={databaseId} database={database}/>

    </Stack>


    <CollapsibleSection
      title={() => {
        return <HStack spacing={3}>
          {configured ? <CheckIcon
            className={"h-4 w-4 ml-0.5 inline text-white bg-green-700 opacity-80 rounded-xl"}/> :
            <XIcon
              className={"h-4 w-4 ml-0.5 p-0.5 inline text-white bg-red-700 opacity-80 rounded-xl"}/>}
          <Text>Properties Configuration</Text>
        </HStack>
      }}
    >

      <VStack alignItems={"start"} spacing={"4"} paddingLeft={"1"}>

        <Box color={"gray.500"} my={1} maxW={"xl"}>
          All supported properties are optional, you can create / delete them on your own or use auto
          configuration to create all of the properties.
        </Box>

        {!configured &&
          <Button variant={"solid"}
            colorScheme={"blue"}
            size={"sm"}
            onClick={() => onAutoconfigure()}
          >
            <span>Autoconfigure default properties</span>
          </Button>}


        {inboxProperties.map(({property, name, description, modify, selectedProperty}, index) => {
          return <HStack key={index} my={1} alignItems={"start"} spacing={2}>
            <Box>
              {property ?
                <CheckIcon
                  className={"h-4 w-4 inline text-white bg-green-700 opacity-80 rounded-xl"}/> :
                <XIcon
                  className={"h-4 w-4 p-0.5 inline text-white bg-red-700 opacity-80 rounded-xl"}/>}
            </Box>
            <VStack alignItems={"start"} spacing={"0"}>
              <Text textColor={"black"} fontWeight={"medium"}>{name}</Text>
              <Text textColor={"gray.700"} maxW={"2xl"}>{description}</Text>

              {/*<Text>Selected property: {selectedProperty || ""} / {property}</Text>*/}
              <Stack direction={"row"} spacing={2} alignItems={"center"}>
                <Text>Select property name:</Text>
                <Flex w={"200px"} fontSize={"sm"}>
                  <DropdownSelector
                    value={property || selectedProperty}
                    options={database.properties}
                    onChange={(dataObject) => {
                      if (modify && dataObject) {
                        const updated = modify(inboxConfiguration.customFieldNames, dataObject.value)
                        onConfigurationChange({...inboxConfiguration, customFieldNames: updated})
                      }
                    }}/>
                </Flex>
              </Stack>

            </VStack>
          </HStack>
        })}

        <VStack alignItems={"start"} w={'full'} my={4} spacing={4}>
          <Text fontSize={"lg"} fontWeight={"medium"}>Threading Configuration</Text>
          <CardRow left={() => {
            return <Box>
              <Text textColor={"black"} fontWeight={"medium"}>Reply Linking Method</Text>
              <Text textColor={"gray.700"}>
                Choose how replies in a thread are linked. You can link to either the first message of
                the thread or the most recent reply.
              </Text>
            </Box>
          }}
          right={() => {
            const options = [
              {name: "Link to Most Recent Reply", value: "last_reply"},
              {name: "Link to First Message of Thread", value: "first_reply"}
            ];
            const optionValues = options.map(x => x.name)
            const currentValue = options.find(x => x.value === (inboxConfiguration.inheritParentType || DEFAULT_INHERIT_PARENT_TYPE))

            return <Box w={{base: "sm", md: "md"}}>
              <DropdownSelector options={optionValues}
                value={currentValue?.name || ""}
                onChange={(newOption) => {
                  const newValue = newOption?.value
                  const updated = options.find(x => x.name === newValue)
                  if (onConfigurationChange) {
                    event({
                      action: "reply_link_configuration_update",
                      params: {
                        targetValue: updated?.value
                      }
                    })
                    onConfigurationChange({
                      ...inboxConfiguration,
                      // @ts-ignore
                      inheritParentType: (updated?.value ?? DEFAULT_INHERIT_PARENT_TYPE)
                    })
                  }
                }}
              />
            </Box>

          }}/>

          <CardRow left={() => {
            return <Box>
              <Text textColor={"black"} fontWeight={"medium"}>Inherited Email Properties</Text>
              <Text textColor={"gray.700"}>
                Define which properties from a previous email in the thread (based on the 'Reply To'
                field) should be copied to the new email.
              </Text>
            </Box>
          }}
          right={() => {
            const currentProps = inboxConfiguration.inheritParentProperties?.map(x => x.name) ?? []
            const options = getNonReservedPropertiesNames(database)
            return <Box w={{base: "sm", md: "md"}}>
              <MultiSelect options={options}
                defaultValue={currentProps}
                onInputChange={(n) => {
                  if (onConfigurationChange) {
                    event({
                      action: "inherit_properties_update", params: {
                        options: JSON.stringify(n)
                      }
                    })
                    onConfigurationChange({
                      ...inboxConfiguration,
                      inheritParentProperties: n.map(x => ({
                        name: x,
                      }))
                    })

                  }
                }}/>
            </Box>

          }}/>
        </VStack>
      </VStack>

    </CollapsibleSection>

    <CollapsibleSection
      title={() => <HStack>
        <LightningBoltIcon className={"w-5 h-5"}/>
        <Text>Properties Data Extraction</Text>
      </HStack>}
    >
      <Box>
        <Text textColor={"gray.500"} maxW={"xl"}> Extract information from email to Notion properties with
          AI. <br/>
          Define new property and describe it to improve the quality of extracted data.
          Data Extraction supports medium sized emails, below 5000 words. </Text>
      </Box>

      <CardRow
        left={() => {
          return <>
            <Text textColor={"black"} fontWeight={"medium"}>Property Extraction</Text>
            <Text textColor={"gray.700"}>Enable data extraction for upcoming emails.</Text>
          </>
        }}

        right={() => {
          return <>
            <DataExtractionSwitch
              enabled={inboxConfiguration.dataExtractionConfig?.enabled || false}
              onChange={(enabled) => {
                const currentConfig = inboxConfiguration.dataExtractionConfig || DEFAULT_DATA_EXTRACTION_CONFIG
                const updated = {
                  ...currentConfig,
                  enabled
                }
                if (onConfigurationChange) {
                  onConfigurationChange({...inboxConfiguration, dataExtractionConfig: updated})
                }
              }}
            />
          </>
        }}
      />

      <CreateNewAIProperty database={database} onCreateNewAIProperty={onCreateNewAIProperty}/>

      {inboxConfiguration.dataExtractionConfig?.properties &&
        <VStack alignItems={"start"} spacing={4}>
          <VStack alignItems={"start"} spacing={1} mb={2}>
            <Text textColor={"black"} fontWeight={"medium"}>Existing Data Properties:</Text>
            <Text textColor={"gray.700"} maxW={"xl"}>AI will analyze email to extract properties
              information</Text>
          </VStack>

          {inboxConfiguration.dataExtractionConfig?.properties?.map((p) => {
            const matchingProperty = database.propertiesObjects.find((dbP) => dbP.name?.toLowerCase() === p.propertyName?.toLowerCase())
            return <HStack key={p.propertyId} marginY={"0"} alignItems={"start"} spacing={"2"}>
              <Box>
                {matchingProperty ?
                  <CheckIcon
                    className={"h-4 w-4 inline text-white bg-green-700 opacity-80 rounded-xl"}/> :
                  <XIcon
                    className={"h-4 w-4 p-0.5 inline text-white bg-red-700 opacity-80 rounded-xl"}/>}
              </Box>
              <VStack alignItems={"start"} spacing={"0"}>
                <Text textColor={"black"} fontWeight={"medium"}>{p.propertyName}</Text>
                <Text textColor={"gray.700"} maxW={"2xl"}>Description: {p.propertyDescription}</Text>
                <Button variant={"unstyled"} size={"xs"} my={1} onClick={() => {
                  if (onDropAIProperty) {
                    event({action: "remove_default_property", params: {}})
                    onDropAIProperty(p.propertyName)
                  }
                }}> <TrashIcon width={16}/></Button>

              </VStack>
            </HStack>
          })}
        </VStack>
      }

    </CollapsibleSection>

    <CollapsibleSection
      title={() => <HStack>
        <DatabaseIcon className={"w-5 h-5"}/>
        <Text>Properties Default Values</Text>
      </HStack>}

    >
      <Text color={"gray.500"} maxW={"xl"}>Configure default values for your custom properties. These values will be applied to all incoming email pages when saved.</Text>

      <DefaultPropertiesModal
        database={database}
        inboxConfiguration={inboxConfiguration}
        onNewProperty={(newProperty: DefaultNotionProperty) => {
          if (onCreateNewDefaultProperty) {

            return onCreateNewDefaultProperty(newProperty)
          }
          return Promise.resolve()
        }}/>

      {inboxConfiguration.defaultPropertiesConfig?.properties?.map((p) => {
        const matchingProperty = database.propertiesObjects.find((dbP) => dbP.name?.toLowerCase() === p.name?.toLowerCase())

        let stringValue = null
        if(typeof p.defaultValue === "string"){
          stringValue = p.defaultValue
        }
        if(typeof p.defaultValue === "object"){
          if(p.defaultValue?.value){
            stringValue = p.defaultValue?.value
          }

          if(Array.isArray(p.defaultValue)){
            stringValue = p.defaultValue?.map(x => x?.value).join(", ")
          }

          if(p.defaultValue === null && p.type == "checkbox") {
            stringValue = "false"
          }
        }


        return <HStack key={p.name} marginY={"0"} alignItems={"start"} spacing={"2"}>
          <Box>
            {matchingProperty ?
              <CheckIcon
                className={"h-4 w-4 inline text-white bg-green-700 opacity-80 rounded-xl"}/> :
              <XIcon
                className={"h-4 w-4 p-0.5 inline text-white bg-red-700 opacity-80 rounded-xl"}/>}
          </Box>
          <VStack alignItems={"start"} spacing={"0"}>
            <Text textColor={"black"} fontWeight={"medium"}>{p.name}</Text>
            <Text textColor={"gray.700"} maxW={"2xl"}>Property Type: {p.type}</Text>
            <Text textColor={"gray.700"} maxW={"2xl"}>Default Value: {stringValue ?? JSON.stringify(p.defaultValue)}</Text>

            <Button variant={"unstyled"} size={"xs"} my={1} onClick={() => {
              if (onDropDefaultProperty) {
                onDropDefaultProperty(p.name)
              }
            }}> <TrashIcon width={16}/></Button>

          </VStack>
        </HStack>

      })}


    </CollapsibleSection>

    {inboxEmail &&
      <Button colorScheme={"red"}
        size={"sm"}
        variant={"outline"}
        onClick={() => {
          if (onDeleteInbox) {
            onDeleteInbox()
          }
        }}>
        <span>Delete inbox</span>
      </Button>
    }
  </VStack>
}

